import { API_BASE_URL, getAuthHeaders } from './apiConfig';

// Get all applications
export const getApplications = async (searchTerm = '', status = '', page = 1, limit = 20, filters = {}) => {
  try {
    let url = `${API_BASE_URL}/applications`;
    const params = new URLSearchParams();
    
    const searchTrimmed = searchTerm != null ? String(searchTerm).trim() : '';
    if (searchTrimmed) {
      params.append('search', searchTrimmed);
    }
    
    if (status) {
      params.append('status', status);
    }

    const { appliedFrom, appliedTo, ageMin, ageMax, daysPreferred, city, campus, filledOut } = filters;
    if (appliedFrom) {
      params.append('applied_from', appliedFrom);
    }
    if (appliedTo) {
      params.append('applied_to', appliedTo);
    }
    if (ageMin !== undefined && ageMin !== '') {
      params.append('age_min', String(ageMin));
    }
    if (ageMax !== undefined && ageMax !== '') {
      params.append('age_max', String(ageMax));
    }
    if (daysPreferred) {
      params.append('days_preferred', daysPreferred);
    }
    const cityTrimmed = city != null && String(city).trim() !== '' ? String(city).trim() : '';
    if (cityTrimmed) {
      params.append('city', cityTrimmed);
    }
    const campusTrimmed = campus != null && String(campus).trim() !== '' ? String(campus).trim() : '';
    if (campusTrimmed) {
      params.append('campus', campusTrimmed);
    }
    const filledOutTrimmed =
      filledOut != null && String(filledOut).trim() !== '' ? String(filledOut).trim().toLowerCase() : '';
    if (filledOutTrimmed === 'online' || filledOutTrimmed === 'office') {
      params.append('filled_out', filledOutTrimmed);
    }
    
    // Add pagination parameters
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await fetch(url, {
      headers: { ...getAuthHeaders() },
      cache: 'no-store',
    });
    const data = await response.json();
   
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch applications');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching applications:', error);
    throw error;
  }
};

// Get application by ID
export const getApplication = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/applications/${id}`, { headers: { ...getAuthHeaders() } });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch application');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching application:', error);
    throw error;
  }
};

// Create a new application
export const createApplication = async (applicationData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/applications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(applicationData),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create application');
    }
    
    return data;
  } catch (error) {
    console.error('Error creating application:', error);
    throw error;
  }
};

// Update an application (supports both JSON and FormData)
export const updateApplication = async (id, applicationData) => {
  try {
    // Check if applicationData is FormData or a regular object
    const isFormData = applicationData instanceof FormData;
    
    const response = await fetch(`${API_BASE_URL}/applications/${id}`, {
      method: 'PUT',
      headers: isFormData ? { ...getAuthHeaders() } : {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: isFormData ? applicationData : JSON.stringify(applicationData),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update application');
    }
    
    return data;
  } catch (error) {
    console.error('Error updating application:', error);
    throw error;
  }
};

// Delete an application
export const deleteApplication = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/applications/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete application');
    }
    
    return data;
  } catch (error) {
    console.error('Error deleting application:', error);
    throw error;
  }
};

// Update application status
export const updateApplicationStatus = async (id, status) => {
  try {
    const response = await fetch(`${API_BASE_URL}/applications/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ status }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update application status');
    }
    
    return data;
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
}; 